import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { isAuthenticated } from "@/lib/auth";

export type AiBudgetStatus = {
  limitUsd: number;
  /** One-off admin grants on this period; the real ceiling is limit + this. */
  topUpUsd: number;
  spentUsd: number;
  remainingUsd: number;
  periodStart: string;
  periodEnd: string;
  exhausted: boolean;
  overrideUsd: number | null;
};

export const AI_BUDGET_QUERY_KEY = ["aiBudget"] as const;

/**
 * What the meter is really measured against: the monthly limit plus whatever an
 * admin granted for this period. Reading `limitUsd` alone would show someone
 * who has just been topped up as over 100% used.
 */
export function budgetCeilingUsd(status: Pick<AiBudgetStatus, "limitUsd" | "topUpUsd">): number {
  return status.limitUsd + (status.topUpUsd ?? 0);
}

export function budgetUsedPct(
  status: Pick<AiBudgetStatus, "limitUsd" | "topUpUsd" | "spentUsd">,
): number {
  const ceiling = budgetCeilingUsd(status);
  return ceiling > 0 ? Math.min(100, (status.spentUsd / ceiling) * 100) : 0;
}

/** Call after any AI turn finishes so the nav usage meter stays current. */
export function useRefreshAiBudget() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AI_BUDGET_QUERY_KEY });
  }, [queryClient]);
}

export function useAiBudget() {
  return useQuery({
    queryKey: AI_BUDGET_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get<AiBudgetStatus>("/auth/ai-budget");
      return res.data;
    },
    enabled: isAuthenticated(),
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}
